import {
  Menu,
  MenuButton,
  MenuItem,
  MenuDivider,
  SubMenu,
} from '@szhsin/react-menu'
import { TaskState, ChecklistState, DateRange } from '../types'
import { useMemo, useState, useCallback, useEffect } from 'react'
import {
  usePrivateProjectNotes,
  useTeamProjectNotes,
} from '../../../hooks/useNotesFactory'
import { Note, NoteType } from '../../../utils/note'

import { sortFoldersByFullPath } from '../../../utils/sorting'
import { useSidebarProvider } from '../../../contexts/Sidebar'
import { getBreadcrumbArray } from '../../../utils/breadcrumb'

type FilterConfig = {
  noteType?: NoteType[]
  taskState?: TaskState[]
  checklistState?: ChecklistState[]
  dateRange?: DateRange
  folder?: string[]
}

type UnifiedFilterDropdownProps = {
  filterConfig: FilterConfig
  onFilterChange: (filters: Partial<FilterConfig>) => void
}

// Task state groups
const taskStates = [
  { value: TaskState.OPEN, label: 'Open' },
  { value: TaskState.DONE, label: 'Done' },
  { value: TaskState.SCHEDULED, label: 'Scheduled' },
  { value: TaskState.CANCELLED, label: 'Cancelled' },
]

// Checklist state groups
// const checklistStates = [
//   { value: ChecklistState.OPEN, label: 'Open' },
//   { value: ChecklistState.DONE, label: 'Done' },
//   { value: ChecklistState.SCHEDULED, label: 'Scheduled' },
//   { value: ChecklistState.CANCELLED, label: 'Cancelled' },
// ]

// Date range options
const dateRangeOptions = [
  { value: 'thisWeek', label: 'This Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'thisQuarter', label: 'This Quarter' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'rolling30Days', label: 'Rolling 30 Days' },
  { value: 'future', label: 'Future' },
  { value: 'past', label: 'Past' },
]

export default function UnifiedFilterDropdown({
  filterConfig,
  onFilterChange,
}: UnifiedFilterDropdownProps) {
  // Folder search state for immediate UI feedback
  const [folderSearchTerm, setFolderSearchTerm] = useState('')

  // Debounced folder search term for actual filtering
  const [debouncedFolderSearchTerm, setDebouncedFolderSearchTerm] = useState('')

  // Debounced effect to update the search term used for filtering
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFolderSearchTerm(folderSearchTerm)
    }, 350)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [folderSearchTerm])

  // Get all folders from notes
  const { data: privateProjectNotes } = usePrivateProjectNotes()
  const { data: teamProjectNotes } = useTeamProjectNotes()

  // Get sidebar entries for breadcrumb functionality
  const { sidebarEntries } = useSidebarProvider()

  // Extract all folders from notes with enhanced data
  const folders = useMemo(() => {
    const folderMap = new Map<
      string,
      {
        id: string
        name: string
        filename: string
        fullPath: string
        isTeamSpace: boolean
        breadcrumb: string[]
      }
    >()

    // Helper function to process notes and extract folders
    const processNotes = (
      notes?: { map?: Map<string, Note> },
      isTeamSpace = false
    ) => {
      if (!notes?.map) return

      for (const note of notes.map.values()) {
        if (note.isFolder && note.recordName && note.title) {
          // Exclude directories that start with @
          if (note.title.startsWith('@')) continue

          // Only add folder if it doesn't already exist (private notes processed first)
          if (!folderMap.has(note.recordName)) {
            // Get breadcrumb using sidebar entries (same approach as NoteTitle.tsx)
            const breadcrumbItems = note.recordName
              ? getBreadcrumbArray(sidebarEntries, note.recordName)
              : []

            // Create full path for sorting (breadcrumb + folder name)
            const fullPath =
              breadcrumbItems.length > 0
                ? [...breadcrumbItems, note.title].join('/')
                : note.filename

            folderMap.set(note.recordName, {
              id: note.recordName,
              name: note.title,
              filename: note.filename,
              fullPath,
              isTeamSpace,
              breadcrumb: breadcrumbItems,
            })
          }
        }
      }
    }

    // Process private notes (folders)
    processNotes(privateProjectNotes, false)

    // Process team notes (teamspace folders)
    processNotes(teamProjectNotes, true)

    return [...folderMap.values()].sort(sortFoldersByFullPath)
  }, [privateProjectNotes, teamProjectNotes, sidebarEntries])

  // Filter folders based on debounced search term
  const filteredFolders = useMemo(() => {
    if (!debouncedFolderSearchTerm.trim()) return folders

    return folders.filter(
      (folder) =>
        folder.name
          .toLowerCase()
          .includes(debouncedFolderSearchTerm.toLowerCase()) ||
        folder.breadcrumb.some((crumb) =>
          crumb.toLowerCase().includes(debouncedFolderSearchTerm.toLowerCase())
        )
    )
  }, [folders, debouncedFolderSearchTerm])

  // Helper functions for handling changes
  const handleNoteTypeChange = (noteTypes: NoteType[]) => {
    onFilterChange({ noteType: noteTypes })
  }

  const handleTaskStateChange = (taskState: TaskState) => {
    const currentStates = filterConfig.taskState ?? []
    if (currentStates.includes(taskState)) {
      onFilterChange({
        taskState: currentStates.filter((state) => state !== taskState),
      })
    } else {
      onFilterChange({
        taskState: [...currentStates, taskState],
      })
    }
  }

  // const handleChecklistStateChange = (checklistState: ChecklistState) => {
  //   const currentStates = filterConfig.checklistState ?? []
  //   if (currentStates.includes(checklistState)) {
  //     onFilterChange({
  //       checklistState: currentStates.filter(
  //         (state) => state !== checklistState
  //       ),
  //     })
  //   } else {
  //     onFilterChange({
  //       checklistState: [...currentStates, checklistState],
  //     })
  //   }
  // }

  const handleDateRangeChange = (rangeType: string) => {
    switch (rangeType) {
      case 'all': {
        onFilterChange({ dateRange: undefined })

        break
      }
      case 'thisWeek': {
        onFilterChange({ dateRange: { type: 'thisWeek' } })

        break
      }
      case 'thisMonth': {
        onFilterChange({ dateRange: { type: 'thisMonth' } })

        break
      }
      case 'thisQuarter': {
        onFilterChange({ dateRange: { type: 'thisQuarter' } })

        break
      }
      case 'thisYear': {
        onFilterChange({ dateRange: { type: 'thisYear' } })

        break
      }
      case 'rolling30Days': {
        onFilterChange({ dateRange: { type: 'rolling30Days' } })

        break
      }
      case 'future': {
        onFilterChange({ dateRange: { type: 'future' } })

        break
      }
      case 'past': {
        onFilterChange({ dateRange: { type: 'past' } })

        break
      }
      // No default
    }
  }

  const handleFolderChange = (folderId: string) => {
    const currentFolders = filterConfig.folder ?? []
    if (currentFolders.includes(folderId)) {
      onFilterChange({
        folder: currentFolders.filter((id) => id !== folderId),
      })
    } else {
      onFilterChange({
        folder: [...currentFolders, folderId],
      })
    }
  }

  // Handle folder search input
  const handleFolderSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFolderSearchTerm(event.target.value)
    },
    []
  )

  // Helper functions for checking selection states
  const isNoteTypeSelected = (noteTypes: NoteType[]) => {
    return (
      noteTypes.some((type) => filterConfig.noteType?.includes(type)) || false
    )
  }

  const isTaskStateSelected = (taskState: TaskState) => {
    return filterConfig.taskState?.includes(taskState) ?? false
  }

  // const isChecklistStateSelected = (checklistState: ChecklistState) => {
  //     return filterConfig.checklistState?.includes(checklistState) ?? false
  //   }

  const isDateRangeSelected = (rangeType: string) => {
    return filterConfig.dateRange?.type === rangeType
  }

  const isFolderSelected = (folderId: string) => {
    return filterConfig.folder?.includes(folderId) ?? false
  }

  // Count active filters for display
  const getActiveFilterCount = () => {
    let count = 0
    if (filterConfig.noteType && filterConfig.noteType.length > 0) count++
    if (filterConfig.taskState && filterConfig.taskState.length > 0) count++
    if (filterConfig.checklistState && filterConfig.checklistState.length > 0)
      count++
    if (filterConfig.dateRange) count++
    if (filterConfig.folder && filterConfig.folder.length > 0) count++
    return count
  }

  const activeFilterCount = getActiveFilterCount()

  return (
    <Menu
      menuButton={
        <MenuButton className='inline-flex min-h-[44px] items-center rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 md:min-h-[32px] md:py-1 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'>
          <i className='fa-solid fa-filter mr-2 text-gray-400' />
          Filters
          {activeFilterCount > 0 && (
            <span className='ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100'>
              {activeFilterCount}
            </span>
          )}
          <i className='fa-solid fa-chevron-down ml-2 text-gray-400' />
        </MenuButton>
      }
      className='min-w-56 [&_[role=menuitem]]:flex [&_[role=menuitem]]:min-h-[44px] [&_[role=menuitem]]:items-center [&_[role=menuitem]]:text-base md:[&_[role=menuitem]]:min-h-[32px] md:[&_[role=menuitem]]:text-sm'
    >
      {/* Clear All Filters */}
      {activeFilterCount > 0 && (
        <>
          <MenuItem
            className='text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
            onClick={() => {
              onFilterChange({
                noteType: undefined,
                taskState: undefined,
                checklistState: undefined,
                dateRange: undefined,
                folder: undefined,
              })
            }}
          >
            <i className='fa-solid fa-times mr-2' />
            Clear All Filters
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {/* Note Type Filter */}
      <SubMenu
        label={
          <div className='flex flex-col'>
            <div className='flex items-center'>
              <i className='fa-regular fa-file-text mr-2' />
              Note Type
            </div>
            {filterConfig.noteType && filterConfig.noteType.length > 0 && (
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {isNoteTypeSelected([
                  NoteType.CALENDAR_NOTE,
                  NoteType.TEAM_SPACE_CALENDAR_NOTE,
                ]) && 'Calendar Notes'}
                {isNoteTypeSelected([
                  NoteType.PROJECT_NOTE,
                  NoteType.TEAM_SPACE_NOTE,
                ]) &&
                  (isNoteTypeSelected([
                    NoteType.CALENDAR_NOTE,
                    NoteType.TEAM_SPACE_CALENDAR_NOTE,
                  ])
                    ? ', Regular Notes'
                    : 'Regular Notes')}
              </div>
            )}
          </div>
        }
      >
        <MenuItem
          className={
            !filterConfig.noteType || filterConfig.noteType.length === 0
              ? 'bg-gray-100 dark:bg-gray-700'
              : ''
          }
          onClick={() => {
            onFilterChange({ noteType: undefined })
          }}
        >
          All Notes
        </MenuItem>
        <MenuDivider />
        <MenuItem
          className={
            isNoteTypeSelected([
              NoteType.CALENDAR_NOTE,
              NoteType.TEAM_SPACE_CALENDAR_NOTE,
            ])
              ? 'bg-gray-100 dark:bg-gray-700'
              : ''
          }
          onClick={() => {
            handleNoteTypeChange([
              NoteType.CALENDAR_NOTE,
              NoteType.TEAM_SPACE_CALENDAR_NOTE,
            ])
          }}
        >
          <i className='fa-regular fa-calendar mr-2' />
          Calendar Notes
        </MenuItem>
        <MenuItem
          className={
            isNoteTypeSelected([
              NoteType.PROJECT_NOTE,
              NoteType.TEAM_SPACE_NOTE,
            ])
              ? 'bg-gray-100 dark:bg-gray-700'
              : ''
          }
          onClick={() => {
            handleNoteTypeChange([
              NoteType.PROJECT_NOTE,
              NoteType.TEAM_SPACE_NOTE,
            ])
          }}
        >
          <i className='fa-regular fa-file-text mr-2' />
          Regular Notes
        </MenuItem>
      </SubMenu>

      {/* Task State Filter */}
      <SubMenu
        label={
          <div className='flex flex-col'>
            <div className='flex items-center'>
              <i className='fa-regular fa-check-square mr-2' />
              Task State
            </div>
            {filterConfig.taskState && filterConfig.taskState.length > 0 && (
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {filterConfig.taskState
                  .map(
                    (state) => taskStates.find((s) => s.value === state)?.label
                  )
                  .join(', ')}
              </div>
            )}
          </div>
        }
      >
        <MenuItem
          className={
            !filterConfig.taskState || filterConfig.taskState.length === 0
              ? 'bg-gray-100 dark:bg-gray-700'
              : ''
          }
          onClick={() => {
            onFilterChange({ taskState: undefined })
          }}
        >
          All Task States
        </MenuItem>
        <MenuDivider />

        {taskStates.map((state) => (
          <MenuItem
            key={state.value}
            className={
              isTaskStateSelected(state.value)
                ? 'bg-gray-100 dark:bg-gray-700'
                : ''
            }
            onClick={(event) => {
              // Keep menu open for multi-select
              event.keepOpen = true
              handleTaskStateChange(state.value)
            }}
          >
            <div className='flex items-center'>
              <input
                type='checkbox'
                className='pointer-events-none mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                checked={isTaskStateSelected(state.value)}
                readOnly
              />
              {state.label}
            </div>
          </MenuItem>
        ))}
      </SubMenu>

      {/* Checklist State Filter */}
      {/* <SubMenu
        label={
          <div className='flex flex-col'>
            <div className='flex items-center'>
              <i className='fa-regular fa-list mr-2' />
              Checklist State
            </div>
            {filterConfig.checklistState &&
              filterConfig.checklistState.length > 0 && (
                <div className='text-xs text-gray-500 dark:text-gray-400'>
                  {filterConfig.checklistState
                    .map(
                      (state) =>
                        checklistStates.find((s) => s.value === state)?.label
                    )
                    .join(', ')}
                </div>
              )}
          </div>
        }
      >
        <MenuItem
          className={
            !filterConfig.checklistState ||
            filterConfig.checklistState.length === 0
              ? 'bg-gray-100 dark:bg-gray-700'
              : ''
          }
          onClick={() => {
            onFilterChange({ checklistState: undefined })
          }}
        >
          All Checklist States
        </MenuItem>
        <MenuDivider />

        {checklistStates.map((state) => (
          <MenuItem
            key={state.value}
            className={
              isChecklistStateSelected(state.value)
                ? 'bg-gray-100 dark:bg-gray-700'
                : ''
            }
            onClick={(event) => {
              // Keep menu open for multi-select
              event.keepOpen = true
              handleChecklistStateChange(state.value)
            }}
          >
            <div className='flex items-center'>
              <input
                type='checkbox'
                className='pointer-events-none mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                checked={isChecklistStateSelected(state.value)}
                readOnly
              />
              {state.label}
            </div>
          </MenuItem>
        ))}
      </SubMenu> */}

      {/* Date Range Filter */}
      <SubMenu
        label={
          <div className='flex flex-col'>
            <div className='flex items-center'>
              <i className='fa-regular fa-calendar mr-2' />
              Date Range
            </div>
            {filterConfig.dateRange && (
              <div className='text-xs text-gray-500 dark:text-gray-400'>
                {dateRangeOptions.find(
                  (opt) => opt.value === filterConfig.dateRange?.type
                )?.label ?? 'Custom'}
              </div>
            )}
          </div>
        }
      >
        <MenuItem
          className={
            filterConfig.dateRange ? '' : 'bg-gray-100 dark:bg-gray-700'
          }
          onClick={() => {
            handleDateRangeChange('all')
          }}
        >
          All Dates
        </MenuItem>
        <MenuDivider />
        {dateRangeOptions.map((option) => (
          <MenuItem
            key={option.value}
            className={
              isDateRangeSelected(option.value)
                ? 'bg-gray-100 dark:bg-gray-700'
                : ''
            }
            onClick={() => {
              handleDateRangeChange(option.value)
            }}
          >
            <i className='fa-regular fa-calendar mr-2' />
            {option.label}
          </MenuItem>
        ))}
      </SubMenu>

      {/* Folder Filter */}
      {folders.length > 0 && (
        <SubMenu
          label={
            <div className='flex flex-col'>
              <div className='flex items-center'>
                <i className='fa-regular fa-folder mr-2' />
                Folders
              </div>
              {filterConfig.folder && filterConfig.folder.length > 0 && (
                <div className='text-xs text-gray-500 dark:text-gray-400'>
                  {filterConfig.folder.length === 1
                    ? folders.find((f) => f.id === filterConfig.folder?.[0])
                        ?.name ?? 'Unknown folder'
                    : `${filterConfig.folder.length.toString()} folders selected`}
                </div>
              )}
            </div>
          }
        >
          {/* Search Input */}
          <div className='mb-2 border-b border-gray-200 p-1 dark:border-gray-600'>
            <div className='relative'>
              <i className='fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 transform text-sm text-gray-400' />
              <input
                type='text'
                className='w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-500 dark:bg-gray-600 dark:text-white dark:placeholder-gray-400'
                placeholder='Search folders'
                value={folderSearchTerm}
                spellCheck={false}
                autoCorrect='off'
                autoComplete='off'
                autoCapitalize='off'
                onChange={handleFolderSearchChange}
                onClick={(event) => {
                  event.stopPropagation()
                }}
              />
            </div>
          </div>

          <MenuItem
            className={
              !filterConfig.folder || filterConfig.folder.length === 0
                ? 'bg-gray-100 dark:bg-gray-700'
                : ''
            }
            onClick={() => {
              onFilterChange({ folder: undefined })
            }}
          >
            All Folders
          </MenuItem>

          <MenuDivider />

          {filteredFolders.length === 0 ? (
            <div className='p-3 text-center text-sm text-gray-500 dark:text-gray-400'>
              {folderSearchTerm ? 'No folders found' : 'No folders available'}
            </div>
          ) : (
            <div className='max-h-80 overflow-y-auto'>
              {filteredFolders.map((folder) => (
                <MenuItem
                  key={folder.id}
                  className={
                    isFolderSelected(folder.id)
                      ? 'bg-gray-100 dark:bg-gray-700'
                      : ''
                  }
                  onClick={(event) => {
                    // Keep menu open for multi-select
                    event.keepOpen = true
                    handleFolderChange(folder.id)
                  }}
                >
                  <div className='flex w-full flex-col'>
                    <div className='flex items-center'>
                      <input
                        type='checkbox'
                        className='pointer-events-none mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500'
                        checked={isFolderSelected(folder.id)}
                        readOnly
                      />
                      {folder.isTeamSpace ? (
                        <i className='far fa-screen-users mr-2 text-green-500' />
                      ) : (
                        <i className='fa-regular fa-folder mr-2 text-gray-500' />
                      )}
                      <span className='font-medium'>{folder.name}</span>
                    </div>
                    {/* Breadcrumb */}
                    {folder.breadcrumb.length > 0 && (
                      <div className='ml-6 text-xs text-gray-500 dark:text-gray-400'>
                        {folder.breadcrumb.map((crumb, index) => (
                          <span key={index} className='font-normal'>
                            <span className='opacity-50'>{crumb}</span>
                            {index < folder.breadcrumb.length - 1 && (
                              <i className='fa-solid fa-chevron-right mx-1 scale-75 text-xs opacity-40' />
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </MenuItem>
              ))}
            </div>
          )}
        </SubMenu>
      )}
    </Menu>
  )
}
